import type React from 'react';
import { useTranslation } from 'react-i18next';
import { mergeClasses } from '@fluentui/react-components';
import type { Incident } from 'types';
import { IncidentSeverity, IncidentState } from 'types';
import { useTick } from 'hooks';
import { SOC_COLORS } from 'themes';
import { useUnassignedIncidentsBarStyles } from './UnassignedIncidentsBar.styles';

interface UnassignedIncidentsBarProps {
  incidents: Incident[];
}

function formatSLA(seconds: number): string {
  const neg = seconds < 0;
  const a = Math.abs(seconds);
  const h = Math.floor(a / 3600);
  const m = Math.floor((a % 3600) / 60);
  const sec = a % 60;
  const sign = neg ? '−' : '';
  return h > 0
    ? `${sign}${h}h ${String(m).padStart(2, '0')}m`
    : `${sign}${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function getSlaSeconds(slaDeadline: string): number {
  return Math.floor((new Date(slaDeadline).getTime() - Date.now()) / 1000);
}

function slaColor(seconds: number): string {
  if (seconds < 0) return SOC_COLORS.red;
  if (seconds < 600) return SOC_COLORS.orange;
  return SOC_COLORS.textMid;
}

const SEVERITY_COLORS: Record<IncidentSeverity, { bg: string; text: string }> = {
  [IncidentSeverity.High]: { bg: 'rgba(232,57,74,0.2)', text: '#e8394a' },
  [IncidentSeverity.Medium]: { bg: 'rgba(255,203,5,0.2)', text: '#ffcb05' },
  [IncidentSeverity.Low]: { bg: 'rgba(141,198,63,0.2)', text: '#8dc63f' },
  [IncidentSeverity.Informational]: { bg: 'rgba(141,198,63,0.15)', text: '#8dc63f' },
};

const SEVERITY_LABELS: Record<IncidentSeverity, string> = {
  [IncidentSeverity.High]: 'HIGH',
  [IncidentSeverity.Medium]: 'MED',
  [IncidentSeverity.Low]: 'LOW',
  [IncidentSeverity.Informational]: 'INFO',
};

interface UnassignedCardProps {
  incident: Incident;
  styles: ReturnType<typeof useUnassignedIncidentsBarStyles>;
}

const UnassignedCard: React.FC<UnassignedCardProps> = ({ incident, styles }) => {
  const slaSeconds = getSlaSeconds(incident.slaDeadline);
  const isOverdue = slaSeconds < 0;
  const severityColors = SEVERITY_COLORS[incident.severity];

  const cardClass = mergeClasses(
    styles.card,
    incident.severity === IncidentSeverity.High && styles.cardHighSeverity,
    incident.severity === IncidentSeverity.Medium && styles.cardMediumSeverity,
    (incident.severity === IncidentSeverity.Low || incident.severity === IncidentSeverity.Informational) && styles.cardLowSeverity,
  );

  return (
    <div
      className={cardClass}
      style={{
        animation: isOverdue ? 'cardPulse 2.5s ease-in-out infinite' : 'none',
      }}
    >
      {/* Title */}
      <div className={styles.cardTitle}>
        {incident.title}
      </div>

      {/* SLA and Severity */}
      <div className={styles.cardFooter}>
        <div
          className={styles.cardSla}
          style={{
            color: slaColor(slaSeconds),
            textShadow: isOverdue ? `0 0 8px ${SOC_COLORS.red}88` : 'none',
          }}
        >
          {formatSLA(slaSeconds)}
        </div>
        <div
          className={styles.cardSeverityBadge}
          style={{
            backgroundColor: severityColors.bg,
            color: severityColors.text,
          }}
        >
          {SEVERITY_LABELS[incident.severity]}
        </div>
      </div>
    </div>
  );
};

/**
 * Sorts incidents by severity (higher = more urgent) and remaining SLA (lower = more urgent).
 */
function sortUnassignedIncidents(a: Incident, b: Incident): number {
  // Sort by severity descending (High=3 first, then Medium=2, etc.)
  if (b.severity !== a.severity) {
    return b.severity - a.severity;
  }
  // Then by SLA remaining ascending (less time remaining = higher priority)
  const aSlaRemaining = getSlaSeconds(a.slaDeadline);
  const bSlaRemaining = getSlaSeconds(b.slaDeadline);
  return aSlaRemaining - bSlaRemaining;
}

export const UnassignedIncidentsBar: React.FC<UnassignedIncidentsBarProps> = ({ incidents }) => {
  const { t } = useTranslation();
  const styles = useUnassignedIncidentsBarStyles();
  
  // Force re-render every second for live SLA updates
  useTick(1000);

  // Filter to active unassigned incidents only
  const unassignedIncidents = incidents
    .filter((i) => !i.assignedOperatorId && i.state !== IncidentState.Closed)
    .sort(sortUnassignedIncidents);

  if (unassignedIncidents.length === 0) {
    return null;
  }

  // Estimate max visible items (roughly 5 items in 1 row)
  const maxVisibleItems = 5;
  const hasHiddenItems = unassignedIncidents.length > maxVisibleItems;

  return (
    <div className={styles.root}>
      {/* Header */}
      <div className={styles.header}>
        <span className={styles.headerTitle}>{t('statusPanel.unassignedBar.title')}</span>
        <span className={styles.headerCount}>{unassignedIncidents.length}</span>
      </div>

      {/* Single row list with hidden overflow to the right */}
      <div className={styles.listWrapper}>
        <div className={styles.listContainer}>
          {unassignedIncidents.map((incident) => (
            <UnassignedCard key={incident.incidentId} incident={incident} styles={styles} />
          ))}
        </div>

        {/* More indicator on right when items are hidden */}
        {hasHiddenItems && (
          <div className={styles.moreIndicatorRight}>
            •••
          </div>
        )}
      </div>
    </div>
  );
};
